import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Star, MessageSquare, Lightbulb, Clock, Headphones, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

// StarRating component moved outside main component
const StarRating = ({ value, onChange, label, icon: Icon }) => {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 rounded-full bg-slate-100">
          <Icon className="w-4 h-4 text-slate-600" />
        </div>
        <span className="text-base font-medium text-slate-700">{label}</span>
      </div>
      <div className="flex gap-2 justify-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="transition-transform hover:scale-110 focus:outline-none p-1"
          >
            <Star
              className={`w-10 h-10 transition-colors ${
                star <= value
                  ? 'text-amber-400 fill-amber-400'
                  : 'text-slate-300 hover:text-amber-200'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const SatisfactionPublicPage = () => {
  const { surveyId } = useParams();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [thankYouMessage, setThankYouMessage] = useState('');
  
  // Scores
  const [listeningScore, setListeningScore] = useState(0);
  const [creativityScore, setCreativityScore] = useState(0);
  const [adviceScore, setAdviceScore] = useState(0);
  const [deadlineBudgetScore, setDeadlineBudgetScore] = useState(0);
  const [comment, setComment] = useState('');

  const fetchSurvey = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/satisfaction-surveys/${surveyId}`);
      setSurvey(response.data);
      
      if (response.data.status === 'completed') {
        setSubmitted(true);
        setThankYouMessage('Cette enquête a déjà été complétée. Merci pour votre retour !');
      }
    } catch (err) {
      console.error('Error fetching survey:', err);
      setError(err.response?.data?.detail || 'Enquête non trouvée');
    } finally {
      setLoading(false);
    }
  }, [surveyId]);

  useEffect(() => {
    fetchSurvey();
  }, [fetchSurvey]);

  const handleSubmit = async () => {
    if (listeningScore === 0 || creativityScore === 0 || adviceScore === 0 || deadlineBudgetScore === 0) {
      toast.error('Veuillez noter tous les critères');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/satisfaction-surveys/${surveyId}/submit`, {
        listening_score: listeningScore,
        creativity_score: creativityScore,
        advice_score: adviceScore,
        deadline_budget_score: deadlineBudgetScore,
        comment: comment || null
      });
      
      setThankYouMessage(response.data.message);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
            <p className="text-slate-600">Chargement de l&apos;enquête...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Oups !</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-yellow-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg border-amber-200">
          <CardContent className="p-8 text-center">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Merci !</h2>
            <p className="text-slate-600 text-lg">{thankYouMessage}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalScore = listeningScore + creativityScore + adviceScore + deadlineBudgetScore;
  const canSubmit = listeningScore > 0 && creativityScore > 0 && adviceScore > 0 && deadlineBudgetScore > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center border-b pb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
            <Star className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Votre avis compte !</CardTitle>
          <p className="text-slate-500 mt-2">
            {survey?.project_name ? (
              <>Comment s&apos;est passée votre expérience avec le projet <strong>{survey.project_name}</strong> ?</>
            ) : survey?.task_name ? (
              <>Comment s&apos;est passée votre expérience avec la tâche <strong>{survey.task_name}</strong> ?</>
            ) : (
              <>Comment s&apos;est passée votre expérience ?</>
            )}
          </p>
        </CardHeader>
        
        <CardContent className="pt-6">
          <StarRating
            value={listeningScore}
            onChange={setListeningScore}
            label="L'écoute"
            icon={Headphones}
          />

          <StarRating
            value={creativityScore}
            onChange={setCreativityScore}
            label="La créativité"
            icon={Lightbulb}
          />

          <StarRating
            value={adviceScore}
            onChange={setAdviceScore}
            label="Le conseil"
            icon={MessageSquare}
          />

          <StarRating
            value={deadlineBudgetScore}
            onChange={setDeadlineBudgetScore}
            label="Le respect du délai et du budget"
            icon={Clock}
          />

          {canSubmit && (
            <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
              <div className="flex items-center justify-between">
                <span className="font-medium text-amber-800">Votre note globale</span>
                <span className="text-3xl font-bold text-amber-600">{totalScore}/20</span>
              </div>
            </div>
          )}

          <div className="mb-6">
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              Un commentaire ? (optionnel)
            </label>
            <Textarea
              placeholder="Partagez votre expérience..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="w-full h-12 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-lg"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <Star className="w-5 h-5 mr-2" />
            )}
            Envoyer mon avis
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SatisfactionPublicPage;
